/**
 * Central client-side error reporter.
 *
 * Captures uncaught exceptions, unhandled promise rejections, and Vue's
 * lifecycle errors, then forwards them (best-effort, deduped, throttled)
 * to the backend bug-report endpoint tagged as `frontend-crash`.
 *
 * Also maintains a small in-memory ring buffer of console logs and
 * user actions ("breadcrumbs") that gets attached to each report so
 * we can reconstruct what the user was doing before the crash.
 */

import { supportAPI } from '@/services/api'

const MAX_BREADCRUMBS = 40
const REPORT_THROTTLE_MS = 5000
const DEDUP_WINDOW_MS = 60_000

const breadcrumbs = []
const recentSignatures = new Map()
let lastReportAt = 0

function addBreadcrumb(level, message) {
    breadcrumbs.push({ level, message: String(message).slice(0, 400), at: new Date().toISOString() })
    if (breadcrumbs.length > MAX_BREADCRUMBS) breadcrumbs.shift()
}

export function trackAction(message) {
    addBreadcrumb('action', message)
}

function signatureFor(error) {
    const msg = error?.message || String(error || 'unknown')
    const stackFirst = String(error?.stack || '').split('\n')[1] || ''
    return `${msg}::${stackFirst}`.slice(0, 500)
}

function shouldReport(sig) {
    const now = Date.now()
    // Global throttle to avoid a crash loop hammering the backend.
    if (now - lastReportAt < REPORT_THROTTLE_MS) return false
    const last = recentSignatures.get(sig) || 0
    if (now - last < DEDUP_WINDOW_MS) return false
    recentSignatures.set(sig, now)
    lastReportAt = now
    // Occasional cleanup so the map doesn't grow forever.
    if (recentSignatures.size > 100) {
        for (const [k, v] of recentSignatures) {
            if (now - v > DEDUP_WINDOW_MS) recentSignatures.delete(k)
        }
    }
    return true
}

export function getBreadcrumbs() {
    return breadcrumbs.slice()
}

async function sendReport(payload) {
    try {
        await supportAPI.reportBug(payload)
    } catch {
        // Reporting must never itself throw — degrade silently.
    }
}

export function reportError(error, context = {}) {
    const sig = signatureFor(error)
    if (!shouldReport(sig)) return

    const message = error?.stack || error?.message || String(error)
    sendReport({
        source: context.source || 'frontend-crash',
        severity: context.severity || 'high',
        message,
        page: typeof document !== 'undefined' ? document.title : '',
        url: typeof window !== 'undefined' ? window.location.href : '',
        userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
        context: { ...(context.context || {}), errorName: error?.name || null },
        logs: getBreadcrumbs(),
    })
}

export function installGlobalErrorHandlers(app) {
    // Vue lifecycle errors (render, watchers, computed, event handlers).
    app.config.errorHandler = (err, _instance, info) => {
        console.error('[Vue error]', info, err)
        reportError(err, { source: 'vue-error', context: { info } })
    }

    if (typeof window !== 'undefined') {
        window.addEventListener('error', (event) => {
            reportError(event.error || new Error(event.message), {
                source: 'window-error',
                context: { filename: event.filename, lineno: event.lineno, colno: event.colno },
            })
        })
        window.addEventListener('unhandledrejection', (event) => {
            const reason = event.reason instanceof Error ? event.reason : new Error(String(event.reason))
            reportError(reason, { source: 'unhandled-rejection' })
        })
    }

    // Lightweight console tap for breadcrumbs — do NOT swallow output.
    if (typeof console !== 'undefined') {
        const originalError = console.error.bind(console)
        console.error = (...args) => {
            addBreadcrumb('error', args.map((a) => (a instanceof Error ? a.message : String(a))).join(' '))
            originalError(...args)
        }
        const originalWarn = console.warn.bind(console)
        console.warn = (...args) => {
            addBreadcrumb('warn', args.map((a) => String(a)).join(' '))
            originalWarn(...args)
        }
    }
}
